/**
 * T573 (FR-UI / A1.6) — Persisted page-size preference.
 *
 * Backed by localStorage so the user's "Belege pro Seite" choice
 * survives reloads. URL state overrides localStorage (so a shared
 * link with `?limit=200` keeps that value even if the local cursor
 * was 50). Use `usePageSizePref('expense_list_page_size', urlLimit)`
 * — pass the URL-derived `limit` so the hook can pick the right
 * winner.
 */
import { useCallback, useEffect, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

const DEFAULT_PAGE_SIZE: PageSizeOption = 50;

export function isValidPageSize(n: unknown): n is PageSizeOption {
  return PAGE_SIZE_OPTIONS.includes(n as PageSizeOption);
}

export function readPageSizePref(storageKey: string): PageSizeOption {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return DEFAULT_PAGE_SIZE;
    const parsed = Number.parseInt(raw, 10);
    return isValidPageSize(parsed) ? parsed : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export function writePageSizePref(storageKey: string, value: PageSizeOption): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    // localStorage may be disabled (private window, quota) — degrade silently.
  }
}

export interface UsePageSizePrefResult {
  pageSize: PageSizeOption;
  setPageSize: (next: PageSizeOption) => void;
}

/**
 * Returns the effective page-size and a setter that persists to
 * localStorage. URL-state wins: if `urlLimit` is a valid page-size
 * option, it is returned as the effective value regardless of what's
 * in localStorage. Calling `setPageSize` always persists.
 */
export function usePageSizePref(
  storageKey: string,
  urlLimit?: number
): UsePageSizePrefResult {
  const [stored, setStored] = useState<PageSizeOption>(() =>
    readPageSizePref(storageKey)
  );

  // Effective value: URL wins when present + valid; otherwise stored cursor.
  const pageSize: PageSizeOption =
    isValidPageSize(urlLimit) ? (urlLimit as PageSizeOption) : stored;

  // If the URL carried a valid size, mirror it into localStorage so
  // the next visit without an explicit URL param remembers what the
  // user actually used.
  useEffect(() => {
    if (isValidPageSize(urlLimit) && urlLimit !== stored) {
      writePageSizePref(storageKey, urlLimit as PageSizeOption);
      setStored(urlLimit as PageSizeOption);
    }
  }, [urlLimit, stored, storageKey]);

  const setPageSize = useCallback(
    (next: PageSizeOption) => {
      writePageSizePref(storageKey, next);
      setStored(next);
    },
    [storageKey]
  );

  return { pageSize, setPageSize };
}
